/**
 * Schémas TypeBox du référentiel d'activités professionnelles + table de
 * jointure `professional_activity`. Cf. docs/professional-activities-plan.md
 * §4 et §6. Source de vérité unique pour la validation backend, la doc
 * Swagger et les types frontend (via Orval).
 */

import { Type, Static } from '@sinclair/typebox';

/** Cap applicatif : 5 activités max par pro (D-4). */
export const MAX_PROFESSIONAL_ACTIVITIES = 5;

/** Cap applicatif : max chars pour le custom_label (TITLE max twitter card - nom du pro). */
export const MAX_CUSTOM_LABEL_LENGTH = 160;

/**
 * Activité côté API publique (catalogue, recommandations) — données light
 * suffisantes pour l'affichage dans le picker sans charger l'activité complète.
 */
export const ActivityPublicSchema = Type.Object({
  activity_id: Type.Integer(),
  slug: Type.String({ maxLength: 120 }),
  default_label: Type.String({ maxLength: 120 }),
  category_slug: Type.String({ maxLength: 80 }),
  popularity_score: Type.Integer({ minimum: 0 }),
  /** Libellés alternatifs (« prothésiste ongulaire » → Manucure) : la recherche locale du quiz les voit. */
  synonyms: Type.Optional(Type.Array(Type.String({ maxLength: 120 }))),
});
export type ActivityPublicObject = Static<typeof ActivityPublicSchema>;

/**
 * Catégorie d'activités avec ses activités imbriquées — payload de
 * `GET /public/activity-catalog` (1 round-trip pour le picker).
 */
export const ActivityCategoryWithChildrenSchema = Type.Object({
  activity_category_id: Type.Integer(),
  slug: Type.String({ maxLength: 80 }),
  default_label: Type.String({ maxLength: 120 }),
  icon_emoji: Type.Union([Type.String({ maxLength: 10 }), Type.Null()]),
  display_order: Type.Integer(),
  /** Secteur de la catégorie (`beaute`, `bien_etre`, `coach_sport`…) : filtre par verticale du quiz. */
  sector: Type.Optional(Type.String({ maxLength: 60 })),
  activities: Type.Array(ActivityPublicSchema),
});
export type ActivityCategoryWithChildrenObject = Static<typeof ActivityCategoryWithChildrenSchema>;

/** Réponse de `GET /public/activity-catalog`. */
export const ActivityCatalogResponseSchema = Type.Object({
  categories: Type.Array(ActivityCategoryWithChildrenSchema),
});
export type ActivityCatalogResponseObject = Static<typeof ActivityCatalogResponseSchema>;

/** Source de la recommandation — utile au frontend pour adapter le wording. */
export enum ActivityRecommendationSource {
  FUZZY_MATCH = 'FUZZY_MATCH',
  SECTOR_TOP = 'SECTOR_TOP',
  GLOBAL_TOP = 'GLOBAL_TOP',
}

/** Réponse de `GET /public/activity-catalog/recommend?metier=…`. */
export const ActivityRecommendationResponseSchema = Type.Object({
  source: Type.Enum(ActivityRecommendationSource),
  activities: Type.Array(ActivityPublicSchema, { maxItems: 10 }),
});
export type ActivityRecommendationResponseObject = Static<typeof ActivityRecommendationResponseSchema>;

/**
 * Activité résolue — projection métier consommée par les pages publiques
 * (cf. plan §6.4). `display_label` est l'angle humain (custom_label > default),
 * `canonical_label` est l'angle machine (toujours le default_label pour le
 * JSON-LD, le filtre annuaire, le search). Voir D-1 du plan pour la décision
 * hybride.
 */
export const ResolvedActivitySchema = Type.Object({
  activity_id: Type.Integer(),
  slug: Type.String({ maxLength: 120 }),
  category_slug: Type.String({ maxLength: 80 }),
  display_label: Type.String({ maxLength: 160 }),
  canonical_label: Type.String({ maxLength: 120 }),
  seo_label_with_article: Type.String({ maxLength: 140 }),
  seo_label_plural: Type.String({ maxLength: 140 }),
  seo_label_feminine: Type.String({ maxLength: 140 }),
  seo_label_practice: Type.String({ maxLength: 140 }),
  schema_org_type: Type.String({ maxLength: 80 }),
  is_primary: Type.Boolean(),
});
export type ResolvedActivityObject = Static<typeof ResolvedActivitySchema>;

/**
 * Contexte SEO dérivé du tableau de `ResolvedActivity[]` — exposé via
 * `seo_globals.scope_context.activity_context` (cf. plan SEO scope §6.1
 * ligne 322). Consommé par `<title>`, meta description, `alt` images,
 * JSON-LD `additionalType` / `knowsAbout`.
 */
export const SeoActivityContextSchema = Type.Object({
  /** `custom_label ?? default_label` de l'activité principale (display_order = 0). */
  primary_display: Type.String({ maxLength: 160 }),
  /** `default_label` de l'activité principale — toujours canonique. */
  primary_canonical: Type.String({ maxLength: 120 }),
  /** "une coiffeuse" — explicite (seedé) ou heuristique. */
  primary_with_article: Type.String({ maxLength: 160 }),
  /** "Coiffeurs" — pluriel pour pages liste. */
  primary_plural: Type.String({ maxLength: 160 }),
  /** "Coiffure" — nom de l'art, pour templates "Salon de {practice}". */
  primary_practice: Type.String({ maxLength: 160 }),
  /** "Beauté" — `activity_category.default_label` de l'activité principale. */
  primary_category_label: Type.String({ maxLength: 120 }),
  /** "Coiffeuse visagiste, Coloriste, Barbier" — display labels concaténés. */
  all_displays_inline: Type.String({ maxLength: 800 }),
  /** "Coiffeur, Coloriste, Barbier" — canonical labels concaténés. */
  all_canonicals_inline: Type.String({ maxLength: 800 }),
  /** ["HairSalon", "BeautySalon"] — pour JSON-LD `additionalType`. */
  schema_org_types: Type.Array(Type.String({ maxLength: 80 })),
  /** ["Coiffeur", "Coloriste", "Barbier"] — pour JSON-LD `knowsAbout`. */
  schema_knows_about: Type.Array(Type.String({ maxLength: 120 })),
});
export type SeoActivityContextObject = Static<typeof SeoActivityContextSchema>;

/** Activité du pro telle qu'exposée par `GET /app/professional/activities`. */
export const ProfessionalActivitySchema = Type.Object({
  activity_id: Type.Integer(),
  display_order: Type.Integer({ minimum: 0 }),
  custom_label: Type.Union([Type.String({ maxLength: MAX_CUSTOM_LABEL_LENGTH }), Type.Null()]),
  activity: ActivityPublicSchema,
});
export type ProfessionalActivityObject = Static<typeof ProfessionalActivitySchema>;

export const ProfessionalActivitiesListSchema = Type.Array(ProfessionalActivitySchema, {
  maxItems: MAX_PROFESSIONAL_ACTIVITIES,
});

/** Body de `POST /app/professional/activities`. */
export const AddProfessionalActivitySchema = Type.Object({
  activity_id: Type.Integer({ minimum: 1 }),
  custom_label: Type.Optional(Type.String({ minLength: 1, maxLength: MAX_CUSTOM_LABEL_LENGTH })),
});
export type AddProfessionalActivityObject = Static<typeof AddProfessionalActivitySchema>;

/** Body de `PATCH /app/professional/activities/:activity_id`. */
export const PatchProfessionalActivitySchema = Type.Object({
  custom_label: Type.Optional(
    Type.Union([Type.String({ minLength: 1, maxLength: MAX_CUSTOM_LABEL_LENGTH }), Type.Null()]),
  ),
});
export type PatchProfessionalActivityObject = Static<typeof PatchProfessionalActivitySchema>;

/** Body de `POST /app/professional/activities/reorder` — IDs dans l'ordre désiré. */
export const ReorderProfessionalActivitiesSchema = Type.Object({
  activity_ids: Type.Array(Type.Integer({ minimum: 1 }), {
    minItems: 1,
    maxItems: MAX_PROFESSIONAL_ACTIVITIES,
  }),
});
export type ReorderProfessionalActivitiesObject = Static<typeof ReorderProfessionalActivitiesSchema>;
